import React from 'react';
import { Box, Typography } from '@mui/material';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay, EffectFade } from 'swiper/modules';
import ReactMarkdown from 'react-markdown';
import LinkButton from '../LinkButton';
import config from 'config/config';
import { HeroCarouselItem } from 'Pages/Main/cmsTypes';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';

interface CarouselProps {
  items?: HeroCarouselItem[];
}

const Carousel = ({ items }: CarouselProps) => {
  return (
    <Box
      sx={{
        minHeight: { xs: '750px', md: '550px' },
        height: { xs: '750px', md: '550px' },
        position: 'relative',
        overflow: 'hidden',
        '& .swiper': {
          width: '100%',
          height: { xs: '750px', md: '550px' },
        },
        '& .swiper-pagination': {
          bottom: '10px',
        },
        '& .swiper-pagination-fraction, & .swiper-pagination-custom, & .swiper-horizontal > .swiper-pagination-bullets, & .swiper-pagination-bullets.swiper-pagination-horizontal':
          {
            left: '0px',
            right: '20px',
            width: 'auto',
            bottom: '0px',
          },
        '& .swiper-pagination-bullet': {
          width: '12px',
          height: '12px',
          backgroundColor: 'rgba(255,255,255,0.4)',
          opacity: 1,
          transition: 'all 0.3s ease',
        },
        '& .swiper-pagination-bullet-active': {
          backgroundColor: '#3b82f6',
          transform: 'scale(1.2)',
        },
      }}
    >
      <Swiper
        modules={[Pagination, Autoplay, EffectFade]}
        spaceBetween={0}
        slidesPerView={1}
        navigation={false}
        pagination={{
          clickable: true,
          dynamicBullets: false,
        }}
        autoplay={
          items && items.length > 1
            ? {
                delay: 4000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }
            : false
        }
        loop={!!items && items.length > 1}
        effect="fade"
        fadeEffect={{
          crossFade: true,
        }}
        speed={800}
      >
        {items?.map((slide) => (
          <SwiperSlide key={slide.id}>
            <Box maxWidth="xl" sx={{ height: { xs: '750px', md: '513px' } }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: 'flex-start',
                  height: '100%',
                  position: 'relative',
                }}
              >
                {/* Left Side - Content */}
                <Box
                  sx={{
                    flex: 1,
                    pr: { xs: 2, md: 6 },
                    pb: { xs: 3, md: 0 },
                    color: 'white',
                    zIndex: 2,
                    minHeight: { xs: 'auto', md: '100%' },
                  }}
                >
                  <ReactMarkdown>{slide.title}</ReactMarkdown>
                  <ReactMarkdown>{slide.header}</ReactMarkdown>

                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '16px',
                      lineHeight: 1.6,
                      mb: 4,
                      opacity: 0,
                      transform: 'translateY(30px)',
                      animation: 'slideInUp 0.8s ease-out 0.6s forwards',
                      paddingBottom: { xs: '20px', md: '20px' },
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: { xs: 5, md: 9 },
                      WebkitBoxOrient: 'vertical',
                      textOverflow: 'ellipsis',
                      maxHeight: { xs: '6.4em', md: '13em' },
                    }}
                  >
                    {slide.description}
                  </Typography>

                  {slide?.buttonLink?.buttonLabel && (
                    <LinkButton
                      href={slide?.buttonLink?.link}
                      sx={{
                        opacity: 0,
                        transform: 'translateY(30px)',
                        animation: 'slideInUp 0.8s ease-out 0.8s forwards',
                      }}
                    >
                      {slide?.buttonLink?.buttonLabel}
                    </LinkButton>
                  )}
                </Box>

                {/* Right Side - Media */}
                <Box
                  sx={{
                    flex: { xs: 'none', md: 1 },
                    width: { xs: '100%', md: 'auto' },
                    height: { xs: '350px', md: '100%' },
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'stretch',
                    justifyContent: 'stretch',
                  }}
                >
                  <Box
                    component="img"
                    src={`${config.CMS_BASE_URL}${slide?.media?.url}`}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      position: 'relative',
                      overflow: 'hidden',
                      opacity: 0,
                      transform: 'scale(0.9)',
                      animation: 'scaleIn 1s ease-out 0.4s forwards',
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Custom CSS animations. Plain <style> tag (not Next.js styled-jsx,
          which isn't wired up in this CRA app) - the `jsx` attribute is
          dropped since it isn't a valid prop and had no effect either way;
          the CSS text still renders as ordinary global styles. */}
      <style>{`
        @keyframes slideInUp {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes scaleIn {
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </Box>
  );
};

export default Carousel;
